import React, { useRef, useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { playerAPI } from '../services/api';
import { buildPlayerTeamWorkspacePath } from '../utils/playerTeamWorkspace';
import { computeAvailabilityReminder } from '../utils/playerAvailabilityReminder';
import PlayerHomeBannerStack from '../components/PlayerHomeBannerStack';
import { buildHelveticLeaguesCreateTeamPath } from '../utils/tournamentRegistrationHelpers';
import {
  FaPencilAlt,
  FaLock,
  FaChevronRight,
  FaUserPlus,
  FaChevronLeft,
  FaUsers,
  FaShieldAlt,
  FaMapMarkerAlt,
  FaMapMarkedAlt,
  FaGraduationCap,
} from 'react-icons/fa';
import RegistrationCountdown from '../components/RegistrationCountdown';

const BRAND = {
  blue: '#043663',
  red: '#D52B1E',
  redHover: '#b52217',
  gray: '#E5E7EB',
  green: '#059669',
};

/** Team workspace deep-link (embedded league dashboard). */
function buildTeamWorkspaceUrl(team, section = 'overview') {
  if (!team?.tenantId || !team?.teamId || !team.latestSeasonId || !team.campaignId) {
    return '/player/teams';
  }
  return buildPlayerTeamWorkspacePath(team.teamId, section, {
    tenant_id: team.tenantId,
    season_id: team.latestSeasonId,
    campaign_id: team.campaignId,
  });
}


function TeamCardThumb({ team }) {
  const [failed, setFailed] = React.useState(false);
  const url = team?.teamLogoUrl;
  return (
    <TeamCardIconWrap $captain={team.isCaptain}>
      {url && !failed ? (
        <img src={url} alt="" onError={() => setFailed(true)} />
      ) : team.isCaptain ? (
        <FaShieldAlt aria-hidden />
      ) : (
        <FaUsers aria-hidden />
      )}
    </TeamCardIconWrap>
  );
}


const WelcomeHeroRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem 1.25rem;
  margin-bottom: 1.35rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.7rem;
    margin-bottom: 1rem;
  }
`;

const WelcomeTextCol = styled.div`
  flex: 1 1 18rem;
  min-width: min(100%, 18rem);
  max-width: 100%;

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    min-width: 0;
  }
`;

const WelcomeBackLine = styled.div`
  margin: 0;
  font-size: clamp(1.25rem, 2.8vw, 1.65rem);
  font-weight: 700;
  color: ${BRAND.blue};
  letter-spacing: -0.02em;
  line-height: 1.25;
  overflow-wrap: break-word;
`;

const WelcomeNameAccent = styled.span`
  color: ${BRAND.red};
`;

const WelcomeSubtitle = styled.p`
  margin: 0.4rem 0 0;
  font-size: 0.9375rem;
  color: #64748b;
  line-height: 1.45;
  max-width: 36rem;
`;

const WelcomeActionsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    flex-shrink: 1;
  }
`;

const WelcomeBtnAccount = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.52rem 1rem;
  border-radius: 10px;
  border: 2px solid ${BRAND.blue};
  background: #fff;
  color: ${BRAND.blue};
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: rgba(4, 54, 99, 0.06);
    color: ${BRAND.blue};
  }

  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    flex: 1 1 9.5rem;
    justify-content: center;
    min-width: 0;
    text-align: center;
    line-height: 1.2;
  }
`;

const WelcomeBtnEdit = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.52rem 1rem;
  border-radius: 10px;
  border: none;
  background: ${BRAND.red};
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 2px 8px rgba(213, 43, 30, 0.35);
  transition:
    background 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    background: ${BRAND.redHover};
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(213, 43, 30, 0.4);
  }

  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    flex: 1 1 9.5rem;
    justify-content: center;
    min-width: 0;
    text-align: center;
    line-height: 1.2;
  }
`;

const ProfileMetaIconRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem 1.35rem;
  margin-top: 0.45rem;
  max-width: 52rem;
`;

const ProfileMetaChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  color: #6b7280;

  svg {
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    color: ${BRAND.blue};
    opacity: 0.85;
  }

  strong {
    font-weight: 600;
    color: #374151;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
`;

const GridRow3 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
`;

const TeamsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const TeamCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
`;

const TeamMemberCard = styled.div`
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1rem 1rem 0.85rem;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const TeamCardAccent = styled.div`
  position: absolute;
  left: 0;
  top: 12px;
  bottom: 12px;
  width: 4px;
  border-radius: 0 4px 4px 0;
  background: ${(p) => (p.$captain ? BRAND.red : 'transparent')};
`;

const TeamCardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  padding-left: ${(p) => (p.$captain ? '8px' : '0')};
`;

const TeamCardIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  background: ${(p) => (p.$captain ? BRAND.blue : 'rgba(4, 54, 99, 0.1)')};
  color: ${(p) => (p.$captain ? '#fff' : BRAND.blue)};
  font-size: 1.1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SeasonRegGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
`;

const SeasonRegCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
`;

const SeasonRegBanner = styled.div`
  height: 96px;
  background: linear-gradient(135deg, #043663 0%, #0a5a9e 100%);
  background-image: ${(p) => (p.$url ? `url(${p.$url})` : 'none')};
  background-size: cover;
  background-position: center;
  position: relative;
`;

const SeasonRegTenantLogo = styled.img`
  position: absolute;
  bottom: -22px;
  left: 14px;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  background: #fff;
`;

const SeasonRegBody = styled.div`
  padding: 2rem 1rem 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const SeasonRegCampaignTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.25;
`;

const SeasonRegSeasonLine = styled.div`
  font-size: 0.82rem;
  color: #6b7280;
`;

const SeasonRegTenantLine = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${BRAND.blue};
  margin-top: 0.25rem;
`;

const SeasonRegFoot = styled.div`
  padding: 0 1rem 1rem;
  margin-top: auto;
`;

const RolePill = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.35rem 0.55rem;
  border-radius: 999px;
  background: rgba(4, 54, 99, 0.1);
  color: ${BRAND.blue};
  flex-shrink: 0;
`;

const TeamCardName = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
  padding-left: ${(p) => (p.$captain ? '8px' : '0')};
`;

const TeamCardMeta = styled.div`
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.4;
  padding-left: ${(p) => (p.$captain ? '8px' : '0')};
`;

const TeamCardBtn = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: auto;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  border: 2px solid #111827;
  background: #fff;
  color: #111827;
  font-size: 0.875rem;
  font-weight: 700;
  text-decoration: none;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  &:hover {
    background: #111827;
    color: #fff;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
`;


const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
  font-family: 'Inter', 'Poppins', sans-serif;
`;

/* Profile Card */
const ProfileCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const ProfileHero = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: space-between;
  gap: 1.15rem;
`;

const ProfileHeroMain = styled.div`
  display: flex;
  align-items: center;
  gap: 1.15rem;
  flex: 1;
  min-width: 0;
`;

const AvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ProfileAvatar = styled.div`
  width: 118px;
  height: 118px;
  border-radius: 50%;
  background: ${BRAND.blue};
  color: white;
  font-size: 2.35rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid ${BRAND.red};
  box-shadow:
    6px 8px 18px rgba(15, 23, 42, 0.12),
    inset 0 2px 2px rgba(255, 255, 255, 0.22);
`;

const ProfileAvatarImg = styled.img`
  width: 118px;
  height: 118px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid ${BRAND.red};
  display: block;
  box-shadow:
    6px 8px 18px rgba(15, 23, 42, 0.12),
    inset 0 2px 2px rgba(255, 255, 255, 0.22);
`;

const ProfileTitleBlock = styled.div`
  min-width: 0;
  flex: 1;
`;

const ProfileDisplayTitle = styled.h2`
  margin: 0 0 0.45rem 0;
  font-size: clamp(1.45rem, 3.2vw, 2rem);
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.03em;
  line-height: 1.15;
`;


/* Tournaments / CTAs */
const btnSmallBlueCss = `
  display: inline-block;
  padding: 0.4rem 0.75rem;
  background: ${BRAND.blue};
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  margin-right: 0.5rem;
  margin-top: 0.25rem;
  &:hover { background: #032a4a; color: white; }
`;

const BtnSmallBlue = styled(Link)`
  ${btnSmallBlueCss}
`;

const BtnSmallBlueA = styled.a`
  ${btnSmallBlueCss}
`;


const TeamListLoading = styled.div`
  font-size: 14px;
  color: #6b7280;
  padding: 0.75rem 0;
`;

const EventTournamentCarouselWrap = styled.div`
  position: relative;
  margin-bottom: 1rem;
  min-width: 0;
`;

const EventTournamentCarousel = styled.div`
  &.event-tournament-carousel {
    display: flex;
    gap: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    padding-bottom: 0.5rem;
    margin: 0;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const EventTournamentBlock = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fafafa;

  .event-tournament-carousel & {
    flex: 0 0 100%;
    scroll-snap-align: start;
    scroll-snap-stop: always;
  }
`;

const EventBanner = styled.div`
  width: 100%;
  height: 120px;
  background: linear-gradient(135deg, #043663 0%, #0a5a9e 100%);
  background-image: ${(p) => (p.$url ? `url(${p.$url})` : 'none')};
  background-size: cover;
  background-position: center;
`;

const EventBody = styled.div`
  padding: 0.75rem 1rem 1rem;
`;

const EventTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
  color: #111827;
  margin-bottom: 0.35rem;
`;

const EventMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.45;
  margin-bottom: 0.5rem;
`;

const EventDesc = styled.p`
  font-size: 13px;
  color: #4b5563;
  margin: 0 0 0.65rem 0;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EventBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
`;

const EventBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  background: ${(p) => (p.$live ? '#d1fae5' : p.$reg ? '#dbeafe' : '#f3f4f6')};
  color: ${(p) => (p.$live ? '#065f46' : p.$reg ? '#1e40af' : '#4b5563')};
`;

const BtnRegister = styled.button`
  padding: 0.45rem 0.9rem;
  background: ${BRAND.green};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-right: 0.5rem;
  &:hover:not(:disabled) {
    background: #047857;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CarouselBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #e5e7eb;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: ${BRAND.blue};
    color: white;
    border-color: ${BRAND.blue};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CarouselBtnPrev = styled(CarouselBtn)`
  left: -8px;
`;

const CarouselBtnNext = styled(CarouselBtn)`
  right: -8px;
`;

const RegisteredNote = styled.span`
  font-size: 13px;
  color: ${BRAND.green};
  font-weight: 600;
`;

const PageLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  color: #6b7280;
  font-size: 15px;
`;

const BtnOutlineRed = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  color: ${BRAND.red};
  border: 2px solid ${BRAND.red};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  margin-top: 0.75rem;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${BRAND.red};
    color: white;
  }
`;

function getInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name[0] || '?').toUpperCase();
}

const EmptyHint = styled.div`
  font-size: 14px;
  color: #9ca3af;
  padding: 0.75rem 0;
`;

export default function PlayerDashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isPlayer = user?.type === 'player';
  const displayName = isPlayer
    ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || t('playerDashboardPage.playerFallback')
    : (user?.teamName || user?.firstName || t('playerDashboardPage.playerFallback'));

  const { data: profileData, isLoading: profileLoading } = useQuery(
    ['playerProfile'],
    () => playerAPI.getProfile(),
    { enabled: !!user?.playerId, staleTime: 60 * 1000, refetchOnWindowFocus: true }
  );
  const profile = profileData?.data;

  const { data: tournamentsData, isLoading: tournamentsLoading } = useQuery(
    ['playerDashboardTournaments'],
    () => playerAPI.getDashboardTournaments(),
    { enabled: !!user?.playerId, staleTime: 60 * 1000, refetchOnWindowFocus: true }
  );
  const { data: teamsData, isLoading: teamsLoading } = useQuery(
    ['playerTeams'],
    () => playerAPI.getTeams(),
    { enabled: !!user?.playerId, staleTime: 60 * 1000, refetchOnWindowFocus: true }
  );

  const myTeams = Array.isArray(teamsData?.data) ? teamsData.data : [];
  const hasRegisteredTeam = myTeams.length > 0;

  const { data: availabilityFortnightAxios } = useQuery(
    ['playerAvailabilityFortnight'],
    () => playerAPI.getAvailabilityFortnight(),
    {
      enabled: !!user?.playerId && hasRegisteredTeam,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );

  const availabilityReminder = useMemo(() => {
    if (!hasRegisteredTeam) return null;
    const raw = availabilityFortnightAxios;
    if (!raw || raw.success === false || raw.enabled === false) return null;
    const { show, variant } = computeAvailabilityReminder(raw.days);
    if (!show || !variant) return null;
    return { variant };
  }, [availabilityFortnightAxios, hasRegisteredTeam]);

  const dashboardLoading = profileLoading || tournamentsLoading || teamsLoading;

  const eventTournaments = useMemo(() => {
    const list = tournamentsData?.data?.eventTournaments ?? [];
    return list.filter((ev) => ev.is_registration_open);
  }, [tournamentsData]);

  const openRegistrationCampaignSeasons =
    tournamentsData?.data?.openRegistrationCampaignSeasons ?? [];
  const registerEventMutation = useMutation(
    (eventId) => playerAPI.registerEventTournament(eventId),
    {
      onSuccess: (res) => {
        toast.success(res?.message || t('playerDashboardPage.toastRegistered'));
        queryClient.invalidateQueries(['playerDashboardTournaments']);
      },
      onError: (e) => {
        toast.error(e.response?.data?.message || t('playerDashboardPage.toastRegisterFail'));
      },
    }
  );

  const homeClub = profile?.homeClub?.trim() || null;
  const homeCanton = profile?.homeCanton?.trim() || null;
  const playerSkillLevel =
    profile?.skillLevel != null && !Number.isNaN(Number(profile.skillLevel))
      ? Number(profile.skillLevel)
      : null;
  const profilePictureUrl = profile?.profilePictureUrl || null;

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const eventTournamentsCarouselRef = useRef(null);
  const scrollEventCarousel = (dir) => {
    const el = eventTournamentsCarouselRef.current;
    if (!el) return;
    const step = el.clientWidth;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  if (dashboardLoading) {
    return (
      <>
        <RegistrationCountdown variant="inline" />
        <WelcomeHeroRow>
          <WelcomeTextCol>
            <WelcomeBackLine>
              <Trans
                i18nKey="playerDashboardPage.welcomeBack"
                values={{ name: displayName }}
                components={{ accent: <WelcomeNameAccent /> }}
              />
            </WelcomeBackLine>
          </WelcomeTextCol>
        </WelcomeHeroRow>
        <PageLoading>{t('playerDashboardPage.loadingDashboard')}</PageLoading>
      </>
    );
  }

  return (
    <>
      <RegistrationCountdown variant="inline" />
      <WelcomeHeroRow>
        <WelcomeTextCol>
          <WelcomeBackLine>
            <Trans
              i18nKey="playerDashboardPage.welcomeBack"
              values={{ name: displayName }}
              components={{ accent: <WelcomeNameAccent /> }}
            />
          </WelcomeBackLine>
          <WelcomeSubtitle>{t('playerDashboardPage.welcomeSubtitle')}</WelcomeSubtitle>
        </WelcomeTextCol>
        <WelcomeActionsBar>
          <WelcomeBtnAccount to="/player/settings/security">
            <FaLock size={13} aria-hidden /> {t('playerDashboardPage.accountSecurity')}
          </WelcomeBtnAccount>
          <WelcomeBtnEdit to="/player/settings/profile">
            <FaPencilAlt size={13} aria-hidden /> {t('playerDashboardPage.editProfile')}
          </WelcomeBtnEdit>
        </WelcomeActionsBar>
      </WelcomeHeroRow>

      <Grid>
        <ProfileCard>
          <ProfileHero>
            <ProfileHeroMain>
              <AvatarWrap>
                {profilePictureUrl ? (
                  <ProfileAvatarImg src={profilePictureUrl} alt="" />
                ) : (
                  <ProfileAvatar>{getInitials(displayName)}</ProfileAvatar>
                )}
              </AvatarWrap>
              <ProfileTitleBlock>
                <ProfileDisplayTitle>{displayName}</ProfileDisplayTitle>
                <ProfileMetaIconRow>
                  <ProfileMetaChip>
                    <FaMapMarkerAlt aria-hidden />
                    <span>
                      {t('playerDashboardPage.homeClub')}{' '}
                      <strong style={{ color: homeClub ? '#374151' : '#9ca3af' }}>{homeClub || '—'}</strong>
                    </span>
                  </ProfileMetaChip>
                  <ProfileMetaChip>
                    <FaMapMarkedAlt aria-hidden />
                    <span>
                      {t('playerDashboardPage.homeCanton')}{' '}
                      <strong style={{ color: homeCanton ? '#374151' : '#9ca3af' }}>{homeCanton || '—'}</strong>
                    </span>
                  </ProfileMetaChip>
                  <ProfileMetaChip>
                    <FaGraduationCap aria-hidden />
                    <span>
                      {t('playerDashboardPage.playerSkill')}{' '}
                      <strong style={{ color: playerSkillLevel != null ? '#374151' : '#9ca3af' }}>
                        {playerSkillLevel != null ? playerSkillLevel : '—'}
                      </strong>
                    </span>
                  </ProfileMetaChip>
                </ProfileMetaIconRow>
              </ProfileTitleBlock>
            </ProfileHeroMain>
          </ProfileHero>
        </ProfileCard>
      </Grid>

      <PlayerHomeBannerStack
        availabilityReminder={availabilityReminder}
        fetchEnabled={!!user?.playerId}
      />

      {(openRegistrationCampaignSeasons.length > 0 || eventTournaments.length > 0) && (
      <GridRow3>
        <Card>
          <CardTitle>{t('playerDashboardPage.cardRegistrationOpen')}</CardTitle>

          {openRegistrationCampaignSeasons.length > 0 && (
            <SeasonRegGrid style={{ marginBottom: eventTournaments.length ? '1.35rem' : 0 }}>
              {openRegistrationCampaignSeasons.map((row) => (
                <SeasonRegCard key={`${row.campaign_id}-${row.id}`}>
                  <SeasonRegBanner $url={row.banner_image_url || ''}>
                    {row.tenant_logo_url ? (
                      <SeasonRegTenantLogo
                        src={row.tenant_logo_url}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}
                  </SeasonRegBanner>
                  <SeasonRegBody>
                    <SeasonRegCampaignTitle>
                      {row.campaign_title || t('playerDashboardPage.leagueCampaignFallback')}
                    </SeasonRegCampaignTitle>
                    <SeasonRegSeasonLine>{row.name}</SeasonRegSeasonLine>
                    <SeasonRegTenantLine>{row.tenant_name}</SeasonRegTenantLine>
                    <SeasonRegSeasonLine style={{ marginTop: 8 }}>
                      {t('playerDashboardPage.registrationCloses', { date: formatDate(row.registration_end) })}
                    </SeasonRegSeasonLine>
                  </SeasonRegBody>
                  <SeasonRegFoot>
                    <BtnSmallBlue
                      to={`/player/tournaments?tenant_id=${encodeURIComponent(row.tenant_id)}`}
                    >
                      {t('playerDashboardPage.leagueHubSignup')}
                    </BtnSmallBlue>
                  </SeasonRegFoot>
                </SeasonRegCard>
              ))}
            </SeasonRegGrid>
          )}

          {eventTournaments.length > 0 && (
            <>
              <div
                style={{
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#9ca3af',
                  marginBottom: '0.65rem',
                }}
              >
                {t('playerDashboardPage.eventTournaments')}
              </div>
              <EventTournamentCarouselWrap>
                {eventTournaments.length > 1 && (
                  <>
                    <CarouselBtnPrev
                      type="button"
                      onClick={() => scrollEventCarousel(-1)}
                      aria-label={t('playerDashboardPage.ariaPrevTournament')}
                    >
                      <FaChevronLeft />
                    </CarouselBtnPrev>
                    <CarouselBtnNext
                      type="button"
                      onClick={() => scrollEventCarousel(1)}
                      aria-label={t('playerDashboardPage.ariaNextTournament')}
                    >
                      <FaChevronRight />
                    </CarouselBtnNext>
                  </>
                )}
                <EventTournamentCarousel
                  ref={eventTournamentsCarouselRef}
                  className={eventTournaments.length > 1 ? 'event-tournament-carousel' : undefined}
                >
                  {eventTournaments.map((ev) => (
                    <EventTournamentBlock key={ev.id}>
                      <EventBanner $url={ev.banner_image_url || ''} role="img" aria-label="" />
                      <EventBody>
                        <EventTitle>{ev.title}</EventTitle>
                        <EventMeta>
                          {ev.tenant_name && <strong>{ev.tenant_name}</strong>}
                          {ev.tenant_name && ' · '}
                          {formatDate(ev.start_date)} – {formatDate(ev.end_date)}
                          {ev.entry_fee_chf != null && ` · CHF ${Number(ev.entry_fee_chf).toFixed(2)}`}
                        </EventMeta>
                        {ev.venue_name && (
                          <EventMeta style={{ marginTop: '-0.25rem' }}>{ev.venue_name}</EventMeta>
                        )}
                        <EventBadgeRow>
                          <EventBadge $reg>{t('playerDashboardPage.cardRegistrationOpen')}</EventBadge>
                          {ev.is_tournament_active && (
                            <EventBadge $live>{t('playerDashboardPage.tournamentLive')}</EventBadge>
                          )}
                        </EventBadgeRow>
                        {ev.description && <EventDesc>{ev.description}</EventDesc>}
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                          {ev.registered ? (
                            <RegisteredNote>{t('playerDashboardPage.registeredNote')}</RegisteredNote>
                          ) : (
                            <BtnRegister
                              type="button"
                              disabled={registerEventMutation.isLoading}
                              onClick={() => registerEventMutation.mutate(ev.id)}
                            >
                              {t('playerDashboardPage.registerForTournament')}
                            </BtnRegister>
                          )}
                          {ev.registration_url && (
                            <BtnSmallBlueA href={ev.registration_url} target="_blank" rel="noopener noreferrer">
                              {t('playerDashboardPage.externalSignup')}
                            </BtnSmallBlueA>
                          )}
                          {ev.contact_email && (
                            <BtnSmallBlueA
                              href={`mailto:${ev.contact_email}?subject=${encodeURIComponent(ev.title)}`}
                            >
                              {t('playerDashboardPage.contact')}
                            </BtnSmallBlueA>
                          )}
                        </div>
                      </EventBody>
                    </EventTournamentBlock>
                  ))}
                </EventTournamentCarousel>
              </EventTournamentCarouselWrap>
            </>
          )}

          <div style={{ marginTop: '1rem' }}>
            <BtnSmallBlue to="/player/tournaments?area=leagues">{t('playerDashboardPage.browseLeagues')}</BtnSmallBlue>
          </div>
        </Card>
      </GridRow3>
      )}


      <Card style={{ marginTop: '1.25rem' }}>
        <TeamsSectionHeader>
          <CardTitle style={{ margin: 0 }}>{t('playerDashboardPage.myTeams')}</CardTitle>
          <BtnOutlineRed to={buildHelveticLeaguesCreateTeamPath()} style={{ marginTop: 0 }}>
            <FaUserPlus size={14} /> {t('playerDashboardPage.createTeamCta')}
          </BtnOutlineRed>
        </TeamsSectionHeader>
        {myTeams.length === 0 ? (
          <EmptyHint>{t('playerDashboardPage.emptyMyTeams')}</EmptyHint>
        ) : (
          <TeamCardsGrid>
            {myTeams.map((team) => (
              <TeamMemberCard key={team.teamId}>
                <TeamCardAccent $captain={team.isCaptain} />
                <TeamCardTop $captain={team.isCaptain}>
                  <TeamCardThumb team={team} />
                  <RolePill>{team.role}</RolePill>
                </TeamCardTop>
                <TeamCardName $captain={team.isCaptain}>{team.teamName}</TeamCardName>
                <TeamCardMeta $captain={team.isCaptain}>
                  {team.activeMemberCount != null
                    ? t('playerDashboardPage.playersCount', { n: team.activeMemberCount })
                    : t('playerDashboardPage.playersUnknown')}
                  {team.divisionName
                    ? ` • ${team.divisionName}`
                    : team.tenantName
                      ? ` • ${team.tenantName}`
                      : ''}
                </TeamCardMeta>
                <TeamCardBtn
                  to={
                    team.canOpenLeagueDashboard
                      ? buildTeamWorkspaceUrl(team, 'overview')
                      : '/player/teams'
                  }
                >
                  {t('playerDashboardPage.openDashboard')}
                </TeamCardBtn>
              </TeamMemberCard>
            ))}
          </TeamCardsGrid>
        )}
      </Card>

    </>
  );
}
